import React from 'react';
import { Link } from 'react-router-dom';
import SpeakingBar from './SpeakingBar.jsx';
import EpisodeStats from './EpisodeStats.jsx';
import WordCloud from './WordCloud.jsx';
import { slugify, buildSpeakerBars, questionSplit, showsQuestionSplit } from '../api.js';

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function EpisodeRow({ episode, accentColor, hostLabel = 'Host', guestLabel = 'Guest' }) {
  const isPending = episode.status !== 'complete' || episode.host_pct == null;

  // Multi-host episodes (e.g. SmartLess) carry a per-speaker breakdown — render
  // each host + guest instead of the flat host/guest bar.
  const speakerBars = episode.speakers
    ?? (episode.speaker_breakdown?.length ? buildSpeakerBars(episode.speaker_breakdown, accentColor, episode.podcast_uuid) : null);

  const { hostQuestions, guestQuestions } = questionSplit(episode);
  const hasStats = !isPending && (
    episode.turn_switches != null || hostQuestions != null
  );
  const hasWords = !isPending && episode.top_words?.length > 0;

  const episodeSlug = slugify(episode.title);
  const detailUrl = `/podcast/${episode.podcast_uuid}/episode/${episodeSlug}`;

  return (
    <div
      id={episode.episode_uuid}
      className="episode-row"
      style={{ '--ep-accent': accentColor }}
      aria-label={
        isPending
          ? `${episode.title} — analysing`
          : `${episode.title}, ${hostLabel} ${episode.host_pct?.toFixed(1)}%`
      }
    >
      <Link to={detailUrl} className="episode-row__title-link">
        <span className="episode-row__title">{episode.title}</span>
        {episode.solo_episode && (
          <span
            className="episode-row__solo-badge"
            title="Host-only episode — no guest"
            aria-label="Solo episode: host only, no guest"
          >
            🎙 Solo
          </span>
        )}
      </Link>

      {episode.flag && (
        <span
          className="pill pill--warning"
          title={episode.flag.reason}
          aria-label={`Low confidence: ${episode.flag.reason}`}
          style={{ alignSelf: 'flex-start' }}
        >
          ⚠ Low confidence
        </span>
      )}

      <div className="episode-row__bar-wrap">
        {isPending ? (
          <PendingBar />
        ) : speakerBars ? (
          <SpeakingBar speakers={speakerBars} />
        ) : (
          <SpeakingBar
            hostPct={episode.host_pct}
            guestPct={episode.guest_pct}
            hostLabel={hostLabel}
            guestLabel={guestLabel}
            accentColor={accentColor}
          />
        )}
      </div>

      {hasStats && (
        <div className="episode-row__meta-row">
          <EpisodeStats
            turnSwitches={episode.turn_switches}
            avgTurnMs={episode.avg_turn_ms}
            longestMonologueMs={episode.longest_monologue_ms}
            hostQuestions={hostQuestions}
            guestQuestions={guestQuestions}
            hostLabel={hostLabel}
            guestLabel={guestLabel}
            showQuestions={showsQuestionSplit(episode)}
          />
        </div>
      )}

      {hasWords && (
        <div className="episode-row__words-row">
          <WordCloud words={episode.top_words} />
        </div>
      )}

      <span className="episode-row__date" aria-label={`Published ${formatDate(episode.published_at)}`}>
        {formatDate(episode.published_at)}
      </span>
    </div>
  );
}

function PendingBar() {
  return (
    <div>
      <div className="speaking-bar speaking-bar--scanning" role="status" aria-label="Analysing episode" />
      <p className="scanning-label" aria-hidden="true">analysing...</p>
    </div>
  );
}
